import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SEARCH_PATH,
  buildSearchUrl,
  getSearchTermFromLocation,
} from "../../../utils/searchUtils";
import "./SiteSearchBar.scss";

const isMacPlatform = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);

const isEditableTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
};

const SiteSearchBar = ({
  variant = "navbar",
  autoFocus = false,
  className,
  placeholder = "Search posts, alerts, and topics…",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);

  const isNavbar = variant === "navbar";
  const inputType = isNavbar ? "text" : "search";
  const shortcutLabel = isMacPlatform() ? "⌘K" : "Ctrl K";
  const showShortcut = isNavbar && !focused && !value;

  useEffect(() => {
    if (isNavbar) {
      return;
    }

    if (location.pathname === SEARCH_PATH) {
      setValue(getSearchTermFromLocation(location));
    }
  }, [location.pathname, location.search, isNavbar]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!isNavbar) return undefined;

    const focusSearch = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        return;
      }

      if (
        event.key === "/" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isEditableTarget(document.activeElement)
      ) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, [isNavbar]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    navigate(buildSearchUrl(trimmed));
    if (isNavbar) {
      setValue("");
      inputRef.current?.blur();
    }
  };

  const handleClear = () => {
    setValue("");
    inputRef.current?.focus();
    if (location.pathname === SEARCH_PATH) {
      navigate(SEARCH_PATH);
    }
  };

  const showClear = !isNavbar && value.length > 0;
  const showSubmit = variant === "hero";

  return (
    <form
      className={classNames(
        "site-search",
        `site-search--${variant}`,
        focused && "is-focused",
        className,
      )}
      onSubmit={handleSubmit}
      role="search"
      aria-label="Site search"
    >
      <div className="site-search__field">
        <span className="site-search__icon" aria-hidden="true">
          <i className="material-icons">search</i>
        </span>
        <input
          ref={inputRef}
          type={inputType}
          className="site-search__input"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          aria-label="Search query"
          aria-keyshortcuts={isNavbar ? "Meta+K Control+K /" : undefined}
          autoComplete="off"
          enterKeyHint="search"
        />
        {showShortcut && (
          <kbd className="site-search__shortcut" aria-hidden="true">
            {shortcutLabel}
          </kbd>
        )}
        {showClear && (
          <button
            type="button"
            className="site-search__clear"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <i className="material-icons">close</i>
          </button>
        )}
        {showSubmit && (
          <button type="submit" className="site-search__submit" aria-label="Search">
            <span className="site-search__submit-text">Search</span>
          </button>
        )}
      </div>
    </form>
  );
};

SiteSearchBar.propTypes = {
  variant: PropTypes.oneOf(["navbar", "hero", "sidebar"]),
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
};

export default SiteSearchBar;
